import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadAstroPdf } from "@/lib/astro";
import {
  Panel, ScoreRing, Spectrum, NoteWheel, StatusDonut, StrengthBars, StatusPill, vizColor,
} from "@/components/viz/ReportViz";
import RemedyCrossSell from "@/components/RemedyCrossSell";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export default function AstroReportPlanetary({ scanId, report, grid }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const exec = report.executive_summary || {};
  const overall = exec.overall_score ?? 0;
  const breakdown = exec.planetary_health_summary || {};
  const profile = report.profile_at_a_glance || [];
  const detailed = report.detailed_planets || [];
  const bodySystems = report.health_mapping?.body_systems || [];

  const spectrum = profile.map((p) => ({ name: p.planet, value: p.score }));
  const octave5 = grid?.["5"] || [];
  const noteWheel = NOTES.map((nt) => {
    const cell = octave5.find((c) => c.note === nt);
    return { note: nt, value: cell ? Math.round(cell.hits) : 0 };
  });

  const doPdf = async () => {
    setPdfLoading(true);
    try { await downloadAstroPdf(scanId, "planetary"); }
    catch { toast.error("Could not generate PDF."); }
    finally { setPdfLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="astro-report-planetary">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl tracking-tight text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>{report.report_title}</h2>
          <p className="text-sm text-slate-400">{report.subtitle}</p>
        </div>
        <Button onClick={doPdf} disabled={pdfLoading} className="rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20" data-testid="astro-pdf-planetary">
          {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} strokeWidth={1.5} />} Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Overall vitality" className="flex flex-col items-center justify-center gap-4">
          <ScoreRing value={overall} label="vitality" />
          <StatusDonut breakdown={breakdown} />
        </Panel>
        <Panel title="Planetary spectrum" className="lg:col-span-2">
          <Spectrum data={spectrum} color="#FBBF24" />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Octave-5 note wheel"><NoteWheel data={noteWheel} color="#FBBF24" /></Panel>
        <Panel title="Planetary strength"><StrengthBars rows={profile.map((p) => ({ name: p.planet, score: p.score, status: p.status }))} /></Panel>
      </div>

      {bodySystems.length > 0 && (
        <Panel title="Body-system mapping">
          <StrengthBars rows={bodySystems.map((b) => ({ name: b.system, score: Math.round(b.score) }))} />
        </Panel>
      )}

      <RemedyCrossSell keywords={["Full Astro-Planet", "Starter Planet", "Complete VoiceStrology"]} />

      <div>
        <h3 className="text-xl mb-4 text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>Your nine planets</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {detailed.map((p, i) => (
            <motion.div key={p.planet} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="rounded-2xl border border-white/[0.06] bg-[#0C1017] p-5" data-testid={`planet-${p.planet}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>{p.planet}</span>
                <StatusPill status={p.status} score={p.score} />
              </div>
              <p className="text-xs text-slate-400 mb-3">{p.archetype} · {p.element}</p>
              <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-3">
                <div className="h-full rounded-full" style={{ width: `${p.score}%`, background: vizColor(p.status), boxShadow: `0 0 8px ${vizColor(p.status)}88` }} />
              </div>
              <div className="text-xs text-slate-400 space-y-1">
                {p.positive_traits?.length > 0 && <p><span className="text-slate-300">Strengths:</span> {p.positive_traits.slice(0, 3).join(", ")}</p>}
                {p.body_parts_governed?.length > 0 && <p><span className="text-slate-300">Governs:</span> {p.body_parts_governed.slice(0, 4).join(", ")}</p>}
                {p.preventive_recommendations?.length > 0 && <p><span className="text-slate-300">Remedy:</span> {p.preventive_recommendations.slice(0, 2).join(" · ")}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
